import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiUrl } from '../utils/apiBase';
import type { LeagueCardData } from '../leagues/league-card';
import {
  type DisciplineFilter,
  type FeedEntry,
  type KindFilter,
  type TournamentItem,
  buildFeed,
  filterEntries,
  groupByMonth,
} from './event-entries';
import { EventRow, MonthSection } from './event-row';

const KIND_CHIPS: { value: KindFilter; label: string }[] = [
  { value: 'ALL', label: 'Wszystko' },
  { value: 'league', label: 'Ligi' },
  { value: 'tournament', label: 'Turnieje' },
];

const DISCIPLINE_CHIPS: { value: DisciplineFilter; label: string }[] = [
  { value: 'ALL', label: 'Każda' },
  { value: 'singles', label: 'Singiel' },
  { value: 'doubles', label: 'Debel' },
  { value: 'mixed', label: 'Mix' },
];

const EventsList: React.FC = () => {
  const [tournaments, setTournaments] = useState<TournamentItem[]>([]);
  const [leagues, setLeagues] = useState<LeagueCardData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showPast, setShowPast] = useState(false);
  const [search, setSearch] = useState('');
  const [discipline, setDiscipline] = useState<DisciplineFilter>('ALL');
  const [kind, setKind] = useState<KindFilter>('ALL');

  useEffect(() => {
    Promise.all([
      fetch(apiUrl('/api/public/events')).then((res) => {
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }

        return res.json() as Promise<{ items: TournamentItem[] }>;
      }),
      fetch(apiUrl('/api/public/leagues')).then((res) => {
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }

        return res.json() as Promise<{ items: LeagueCardData[] }>;
      }),
    ])
      .then(([tournamentData, leagueData]) => {
        setTournaments(tournamentData.items);
        setLeagues(leagueData.items);
        setLoading(false);
      })
      .catch(() => {
        setError('Nie udało się załadować danych.');
        setLoading(false);
      });
  }, []);

  const feed = useMemo(
    () => buildFeed(tournaments, leagues, new Date()),
    [tournaments, leagues]
  );

  const filters = useMemo(() => ({ search, discipline, kind }), [search, discipline, kind]);
  const upcoming = useMemo(() => filterEntries(feed.upcoming, filters), [feed, filters]);
  const ongoing = useMemo(() => filterEntries(feed.ongoing, filters), [feed, filters]);
  const past = useMemo(() => filterEntries(feed.past, filters), [feed, filters]);

  const openSignups = useMemo(
    () => [...upcoming, ...ongoing].filter((entry: FeedEntry) => entry.signupOpen).length,
    [upcoming, ongoing]
  );

  if (loading) {
    return <div className="events-loading">Ładowanie...</div>;
  }

  if (error) {
    return <div className="events-error">{error}</div>;
  }

  function resetFilters() {
    setSearch('');
    setDiscipline('ALL');
    setKind('ALL');
  }

  const filtersActive = Boolean(search.trim()) || discipline !== 'ALL' || kind !== 'ALL';
  const visibleCount = upcoming.length + ongoing.length;
  const totalCount = feed.upcoming.length + feed.ongoing.length;

  return (
    <div className="events-list">
      <div className="events-list-header">
        <div>
          <span className="eyebrow">Wydarzenia</span>
          <h1>Terminarz</h1>
        </div>
        <Link to="/events/calendar" className="event-card-link">
          Widok kalendarza
        </Link>
      </div>

      <div className="leagues-filter">
        <input
          type="search"
          className="leagues-filter__search"
          placeholder="Szukaj ligi, turnieju, klubu lub miejsca…"
          aria-label="Szukaj wydarzenia"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <div className="leagues-filter__chips" role="radiogroup" aria-label="Rodzaj wydarzenia">
          {KIND_CHIPS.map((chip) => (
            <button
              key={chip.value}
              role="radio"
              aria-checked={kind === chip.value}
              className={`leagues-filter__chip${
                kind === chip.value ? ' leagues-filter__chip--active' : ''
              }`}
              onClick={() => setKind(chip.value)}
            >
              {chip.label}
            </button>
          ))}
        </div>
        <div className="leagues-filter__chips" role="radiogroup" aria-label="Dyscyplina">
          {DISCIPLINE_CHIPS.map((chip) => (
            <button
              key={chip.value}
              role="radio"
              aria-checked={discipline === chip.value}
              className={`leagues-filter__chip${
                discipline === chip.value ? ' leagues-filter__chip--active' : ''
              }`}
              onClick={() => setDiscipline(chip.value)}
            >
              {chip.label}
            </button>
          ))}
        </div>
      </div>

      <p className="event-feed__summary">
        <span className="event-feed__summary-count">
          {filtersActive ? `${visibleCount} z ${totalCount}` : totalCount}
        </span>
        {filtersActive ? ' pasujących wydarzeń' : ' aktywnych wydarzeń'}
        {openSignups > 0 && (
          <span className="event-feed__summary-signup">
            <span className="signup-pulse-dot" aria-hidden="true" />
            {openSignups} z otwartymi zapisami
          </span>
        )}
        {filtersActive && (
          <button type="button" className="event-feed__summary-reset" onClick={resetFilters}>
            Wyczyść filtry
          </button>
        )}
      </p>

      {visibleCount === 0 ? (
        <div className="leagues-empty">
          <p>Nic nie pasuje do filtrów.</p>
          <button className="leagues-empty__reset" onClick={resetFilters}>
            Pokaż wszystkie
          </button>
        </div>
      ) : (
        <>
          {upcoming.length > 0 && (
            <div className="event-feed">
              <h2 className="event-feed__title">
                Nadchodzące <span className="event-feed__title-count">{upcoming.length}</span>
              </h2>
              {groupByMonth(upcoming).map((group) => (
                <MonthSection key={group.key} group={group} />
              ))}
            </div>
          )}

          {ongoing.length > 0 && (
            <div className="event-feed">
              <h2 className="event-feed__title">
                Bez zaplanowanego terminu{' '}
                <span className="event-feed__title-count">{ongoing.length}</span>
              </h2>
              <p className="event-feed__hint">
                Ligi w toku — organizator nie ogłosił jeszcze kolejnego terminu.
              </p>
              <ul className="event-rows event-rows--muted">
                {ongoing.map((entry) => (
                  <EventRow
                    key={entry.key}
                    entry={entry}
                    dateCaption={entry.startDate ? 'ostatnia kolejka' : undefined}
                  />
                ))}
              </ul>
            </div>
          )}
        </>
      )}

      {past.length > 0 && (
        <div className="event-feed event-feed--past">
          <button
            type="button"
            className="events-past-toggle"
            onClick={() => setShowPast((prev) => !prev)}
            aria-expanded={showPast}
          >
            {showPast ? 'Ukryj zakończone' : `Zakończone (${past.length})`}
          </button>
          {showPast &&
            groupByMonth(past).map((group) => <MonthSection key={group.key} group={group} />)}
        </div>
      )}
    </div>
  );
};

export default EventsList;
